import {
  ACCESS_ROLE_LABEL,
  ACCESS_ROLE_LEVEL,
  ADMIN_COLLABORATOR_ROLES,
  MODERATOR_COLLABORATOR_ROLES,
  USER_ACCOUNT_TYPE_LABEL,
  USER_STATUS_LABEL,
  type AccessRole,
  type UserAccountType,
} from '@/constants/rbac';
import type { User, UserListItem } from '@/types/user';

const toRoleToken = (role?: string | null): string =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

export const getAccessRole = (
  user: { role?: string | null } | string | null | undefined
): AccessRole => {
  const raw = typeof user === 'object' && user ? user.role : user;
  const token = toRoleToken(raw);

  if (token === 'ADMIN_ROOT' || token === 'ADMIN') return 'ADMIN';
  if (token === 'MODERATOR') return 'MODERATOR';
  if (token === 'CURATOR' || token === 'CREATIVE' || token === 'EDITOR') return 'CURATOR';
  if (token === 'SUPPORT' || token === 'AGENT') return 'SUPPORT';
  if (token === 'CLIENT' || token === 'PARTNER_VIEWER') return 'CLIENT';
  if (token === 'GUEST' || token === 'VIEWER') return 'GUEST';

  return 'CURATOR';
};

export const getUserAccountType = (
  user: UserListItem | { type?: string; role?: string } | null | undefined
): UserAccountType => {
  const explicit = String(user?.type || '').trim().toUpperCase();
  if (explicit === 'CLIENT') return 'CLIENT';
  if (explicit === 'COLLABORATOR') return 'COLLABORATOR';
  return getAccessRole(user?.role) === 'CLIENT' ? 'CLIENT' : 'COLLABORATOR';
};

export const getAccessRoleLabel = (
  user: UserListItem | { role?: string } | string | null | undefined
): string => ACCESS_ROLE_LABEL[getAccessRole(user)];

export const getUserAccountTypeLabel = (
  user: UserListItem | null | undefined
): string => USER_ACCOUNT_TYPE_LABEL[getUserAccountType(user)];

type LinkedPartnerSource = {
  partners?: { id?: string; partner_id?: string; name: string }[] | null;
  partner_ids?: string[] | null;
  partner_id?: string | null;
  all_partners?: boolean;
  role?: string | null;
};

const toPartnerId = (value?: string | null): string | null => {
  const id = String(value || '').trim();
  return id || null;
};

export const getLinkedPartners = (user: LinkedPartnerSource | null | undefined) => {
  const fromPartners = Array.isArray(user?.partners)
    ? user.partners
        .map((partner) => ({
          id: toPartnerId(partner?.id) || toPartnerId(partner?.partner_id) || '',
          name: String(partner?.name || ''),
        }))
        .filter((partner) => partner.id)
    : [];

  if (fromPartners.length > 0) return fromPartners;

  const fromIds = Array.isArray(user?.partner_ids)
    ? user.partner_ids.map((id) => toPartnerId(id)).filter((id): id is string => Boolean(id))
    : [];
  const fallbackId = toPartnerId(user?.partner_id);
  const uniqueIds = [...new Set(fallbackId ? [...fromIds, fallbackId] : fromIds)];

  return uniqueIds.map((id) => ({ id, name: '' }));
};

export const isBrandScopedUser = (
  user: { role?: string | null; all_partners?: boolean } | null | undefined
): boolean => {
  const role = getAccessRole(user);
  if (role === 'ADMIN') return false;
  if (user?.all_partners) return false;
  return (
    role === 'MODERATOR' ||
    role === 'CURATOR' ||
    role === 'SUPPORT' ||
    role === 'CLIENT' ||
    role === 'GUEST'
  );
};

export const resolveBrandOptions = <T extends { id: string; name: string }>(
  user: LinkedPartnerSource | null | undefined,
  catalog: T[]
): T[] => {
  if (!isBrandScopedUser(user)) return catalog;
  const linked = getLinkedPartners(user);
  if (linked.length === 0) {
    const role = getAccessRole(user);
    if (role === 'CLIENT' || role === 'GUEST') return [];
    return catalog;
  }
  const linkedIds = new Set(linked.map((partner) => partner.id));
  const fromCatalog = catalog.filter((partner) => linkedIds.has(partner.id));
  if (fromCatalog.length > 0) return fromCatalog;
  return linked.map((partner) => ({ id: partner.id, name: partner.name }) as T);
};

export const canAccessPartner = (
  user: LinkedPartnerSource | null | undefined,
  partnerId?: string | null
): boolean => {
  if (!user) return false;
  if (!isBrandScopedUser(user)) return true;

  const linkedIds = new Set(getLinkedPartners(user).map((partner) => partner.id));
  if (linkedIds.size === 0) {
    const role = getAccessRole(user);
    return role !== 'CLIENT' && role !== 'GUEST';
  }

  const id = toPartnerId(partnerId);
  if (!id) return false;
  return linkedIds.has(id);
};

export const sharesBrandScope = (
  actor: User | null,
  target: UserListItem | User | null
): boolean => {
  if (!actor || !target) return false;
  if (!isBrandScopedUser(actor)) return true;
  if (target.all_partners) return false;

  const actorPartnerIds = new Set(getLinkedPartners(actor).map((partner) => partner.id));
  const targetPartners = getLinkedPartners(target);

  if (actorPartnerIds.size === 0 || targetPartners.length === 0) {
    const actorRole = getAccessRole(actor);
    return (
      getUserAccountType(target) === 'CLIENT' &&
      (actorRole === 'SUPPORT' || actorRole === 'CURATOR')
    );
  }

  return targetPartners.some((partner) => actorPartnerIds.has(partner.id));
};

export const getUserStatusLabel = (status?: string | null): string =>
  USER_STATUS_LABEL[String(status || '').toUpperCase()] || String(status || '');

export const canActOnUser = (
  actor: User | null,
  target: UserListItem | User | null
): boolean => {
  if (!actor || !target) return false;

  const actorRole = getAccessRole(actor);
  const targetRole = getAccessRole(target);

  if (actorRole === 'CLIENT' || actorRole === 'GUEST') return false;

  if (!sharesBrandScope(actor, target)) return false;

  if (actorRole === 'ADMIN') return true;
  return ACCESS_ROLE_LEVEL[actorRole] > ACCESS_ROLE_LEVEL[targetRole];
};

export const getDisabledUserActionTooltip = (
  actor: User | null,
  target: UserListItem | User | null
): string => {
  if (canActOnUser(actor, target)) return '';
  const targetRole = getAccessRole(target);
  if (targetRole === 'ADMIN') {
    return 'Seu perfil não tem permissão para alterar usuários Admin.';
  }
  if (targetRole === 'MODERATOR') {
    return 'Seu perfil não tem permissão para alterar usuários Moderador.';
  }
  return 'Seu perfil não tem permissão para alterar usuários.';
};

export const getAssignableCollaboratorRoles = (actor: User | null): AccessRole[] => {
  const role = getAccessRole(actor);
  if (role === 'ADMIN') {
    return ADMIN_COLLABORATOR_ROLES;
  }
  if (role === 'MODERATOR') {
    return MODERATOR_COLLABORATOR_ROLES;
  }
  return [];
};

export const toApiUserRole = (role?: string): string | undefined => {
  if (!role) return undefined;
  const token = toRoleToken(role);
  const mapped: Record<string, string> = {
    ADMIN: 'ADMIN',
    ADMIN_ROOT: 'ADMIN',
    MODERATOR: 'MODERATOR',
    CURATOR: 'CURATOR',
    CREATIVE: 'CURATOR',
    SUPPORT: 'SUPPORT',
    AGENT: 'SUPPORT',
    CLIENT: 'CLIENT',
    PARTNER_VIEWER: 'CLIENT',
    GUEST: 'GUEST',
    VIEWER: 'GUEST',
  };
  return mapped[token] ?? token;
};
